import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { libellesTraduits } from '../../i18n/libelles';
import appelService from '../../services/appelService';
import stompClient from '../../services/stompClient';
import { getToken, surChangementJeton } from '../../variables/constants';
import { bipFin, sonnerieEntrante, tonaliteRetour } from './sons';
import FenetreAppel from './FenetreAppel';

/**
 * Appel audio 1-à-1, WebRTC natif, sans bibliothèque tierce.
 *
 * Monté au-dessus des routes : un appel entrant doit sonner sur n'importe quelle
 * page, et un appel en cours doit survivre à la navigation.
 *
 * Signalisation : on envoie en REST (`appelService`), on reçoit sur la file STOMP
 * privée (`/user/queue/notifications`) les événements APPEL_OFFRE, APPEL_REPONSE,
 * APPEL_CANDIDAT_ICE, APPEL_FIN. Les candidats ICE reçus avant la description
 * distante sont mis en attente puis rejoués.
 *
 * Audio uniquement : `getUserMedia({ audio })`, `offerToReceiveVideo: false`.
 *
 * États : 'inactif' | 'appel_sortant' (ça sonne chez l'autre) | 'appel_entrant'
 * (ça sonne ici) | 'connexion' | 'en_cours' | 'termine' (bandeau bref).
 */
const ContexteAppel = createContext(null);
export const useAppel = () => useContext(ContexteAppel);

const MOTIFS_LISIBLES = libellesTraduits('commun:appelProvider.motifs');

// Textes affichés, lus dans la langue courante au moment de l'appel.
const texte = (cle) => i18n.t(`commun:appelProvider.${cle}`);

export default function AppelProvider({ children }) {
  const [etat, setEtat] = useState('inactif');
  const [appel, setAppel] = useState(null); // AppelResponse
  const [micCoupe, setMicCoupe] = useState(false);
  const [debutCommunication, setDebutCommunication] = useState(null);
  const [motifFin, setMotifFin] = useState(null);

  const pc = useRef(null);
  const fluxLocal = useRef(null);
  const audioDistant = useRef(null);
  const candidatsEnAttente = useRef([]);
  const candidatsSortants = useRef([]);
  const appelIdRef = useRef(null);
  const offreEntrante = useRef(null);
  const arreterSon = useRef(null);
  const minuterieSonnerie = useRef(null);
  const appelRef = useRef(null);
  appelRef.current = appel;
  const etatRef = useRef(etat);
  etatRef.current = etat;

  // ── Nettoyage ───────────────────────────────────────────────────────────
  const couperSon = () => { arreterSon.current?.(); arreterSon.current = null; };

  const liberer = useCallback(() => {
    couperSon();
    clearTimeout(minuterieSonnerie.current);
    if (pc.current) { try { pc.current.close(); } catch { /* déjà fermé */ } pc.current = null; }
    if (fluxLocal.current) { fluxLocal.current.getTracks().forEach((t) => t.stop()); fluxLocal.current = null; }
    if (audioDistant.current) { audioDistant.current.srcObject = null; }
    candidatsEnAttente.current = [];
    candidatsSortants.current = [];
    appelIdRef.current = null;
    offreEntrante.current = null;
    setMicCoupe(false);
  }, []);

  const terminerLocalement = useCallback((motifAffiche) => {
    liberer();
    bipFin();
    setMotifFin(motifAffiche);
    setEtat('termine');
    setDebutCommunication(null);
    setTimeout(() => { if (etatRef.current === 'termine') { setEtat('inactif'); setAppel(null); setMotifFin(null); } }, 2500);
  }, [liberer]);

  // ── Média et connexion ──────────────────────────────────────────────────
  const obtenirMicro = async () => {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('NAVIGATEUR');
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false,
      });
    } catch (e) {
      if (e.name === 'NotAllowedError' || e.name === 'SecurityError') throw new Error('PERMISSION');
      if (e.name === 'NotFoundError' || e.name === 'OverconstrainedError') throw new Error('PERIPHERIQUE');
      throw new Error('MICRO');
    }
  };

  const messageErreurMicro = (e) => ({
    PERMISSION: texte('erreursMicro.permission'),
    PERIPHERIQUE: texte('erreursMicro.peripherique'),
    NAVIGATEUR: texte('erreursMicro.navigateur'),
  }[e.message] || texte('erreursMicro.micro'));

  /**
   * Côté appelant, l'identifiant d'appel n'existe qu'après l'envoi de l'offre :
   * les candidats ICE produits entre-temps sont tamponnés puis envoyés.
   */
  const envoyerCandidat = (c) => {
    if (appelIdRef.current) appelService.candidat(appelIdRef.current, c).catch(() => { });
    else candidatsSortants.current.push(c);
  };
  const fixerAppelId = (id) => {
    appelIdRef.current = id;
    const l = candidatsSortants.current; candidatsSortants.current = [];
    l.forEach((c) => appelService.candidat(id, c).catch(() => { }));
  };

  const creerConnexion = async (appelId) => {
    const cfg = await appelService.configIce();
    if (appelId) appelIdRef.current = appelId;
    const connexion = new RTCPeerConnection({ iceServers: cfg.iceServers || [] });
    connexion.onicecandidate = (ev) => {
      if (ev.candidate) envoyerCandidat(ev.candidate.toJSON());
    };
    connexion.ontrack = (ev) => {
      if (audioDistant.current) {
        audioDistant.current.srcObject = ev.streams[0];
        audioDistant.current.play().catch(() => { });
      }
    };
    connexion.onconnectionstatechange = () => {
      const s = connexion.connectionState;
      if (s === 'connected') {
        couperSon();
        setEtat('en_cours');
        setDebutCommunication((d) => d || Date.now());
      } else if (s === 'failed') {
        if (appelIdRef.current) appelService.terminer(appelIdRef.current, 'ECHEC_MEDIA').catch(() => { });
        terminerLocalement(texte('connexionImpossibleEntrePostes'));
      } else if (s === 'disconnected') {
        // Micro-coupure : on laisse 8 s à ICE pour se rétablir avant d'abandonner.
        setTimeout(() => {
          if (pc.current === connexion && connexion.connectionState === 'disconnected') {
            if (appelIdRef.current) appelService.terminer(appelIdRef.current, 'PERTE_CONNEXION').catch(() => { });
            terminerLocalement(texte('connexionPerdue'));
          }
        }, 8000);
      }
    };
    fluxLocal.current.getTracks().forEach((t) => connexion.addTrack(t, fluxLocal.current));
    // Débit audio borné : la VM relaie parfois le flux (TURN), on reste léger.
    connexion.getSenders().forEach((s) => {
      if (s.track?.kind === 'audio') {
        const p = s.getParameters();
        p.encodings = p.encodings?.length ? p.encodings : [{}];
        p.encodings[0].maxBitrate = 32000;
        s.setParameters(p).catch(() => { });
      }
    });
    pc.current = connexion;
    return connexion;
  };

  const rejouerCandidats = async () => {
    const liste = candidatsEnAttente.current;
    candidatsEnAttente.current = [];
    for (const c of liste) {
      try { await pc.current?.addIceCandidate(new RTCIceCandidate(c)); } catch { /* candidat obsolète */ }
    }
  };

  // ── Appeler ─────────────────────────────────────────────────────────────
  const appeler = useCallback(async (destinataireTrackingId, nom, conversationTrackingId) => {
    if (etatRef.current !== 'inactif') { toast.warn(texte('appelDejaEnCours')); return; }
    setEtat('appel_sortant');
    setAppel({ appeleNom: nom, appeleTrackingId: destinataireTrackingId, sortant: true });
    try {
      fluxLocal.current = await obtenirMicro();
    } catch (e) {
      toast.error(messageErreurMicro(e));
      liberer(); setEtat('inactif'); setAppel(null);
      return;
    }
    let cree;
    try {
      const connexion = await creerConnexion(null);
      const offre = await connexion.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
      await connexion.setLocalDescription(offre);
      cree = await appelService.initier(destinataireTrackingId, offre.sdp, conversationTrackingId);
      setAppel(cree);
      fixerAppelId(cree.appelTrackingId);
      arreterSon.current = tonaliteRetour();
      const delai = 45 * 1000;
      minuterieSonnerie.current = setTimeout(() => {
        if (etatRef.current === 'appel_sortant') {
          appelService.terminer(cree.appelTrackingId, 'SANS_REPONSE').catch(() => { });
          terminerLocalement(texte('motifs.SANS_REPONSE'));
        }
      }, delai);
    } catch (e) {
      const msg = e?.response?.data?.message || texte('impossibleLancer');
      toast.error(msg);
      liberer(); setEtat('inactif'); setAppel(null);
    }
    // creerConnexion ne dépend que de refs : stable par construction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liberer, terminerLocalement]);

  // ── Répondre / refuser / raccrocher ─────────────────────────────────────
  const accepter = useCallback(async () => {
    const a = appelRef.current;
    if (!a || etatRef.current !== 'appel_entrant') return;
    couperSon();
    setEtat('connexion');
    try {
      fluxLocal.current = await obtenirMicro();
    } catch (e) {
      toast.error(messageErreurMicro(e));
      appelService.terminer(a.appelTrackingId, 'ECHEC_MEDIA').catch(() => { });
      terminerLocalement(texte('microIndisponible'));
      return;
    }
    try {
      const connexion = await creerConnexion(a.appelTrackingId);
      await connexion.setRemoteDescription({ type: 'offer', sdp: offreEntrante.current });
      await rejouerCandidats();
      const reponse = await connexion.createAnswer();
      await connexion.setLocalDescription(reponse);
      await appelService.repondre(a.appelTrackingId, reponse.sdp);
    } catch (e) {
      toast.error(e?.response?.data?.message || texte('connexionEchouee'));
      appelService.terminer(a.appelTrackingId, 'ECHEC_MEDIA').catch(() => { });
      terminerLocalement(texte('motifs.ECHEC'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminerLocalement]);

  const raccrocher = useCallback((motif) => {
    const a = appelRef.current;
    if (a?.appelTrackingId) appelService.terminer(a.appelTrackingId, motif || 'RACCROCHE').catch(() => { });
    terminerLocalement(etatRef.current === 'appel_entrant' ? texte('motifs.REFUSE') : texte('motifs.TERMINE'));
  }, [terminerLocalement]);

  const basculerMicro = useCallback(() => {
    const piste = fluxLocal.current?.getAudioTracks()[0];
    if (!piste) return;
    piste.enabled = !piste.enabled;
    setMicCoupe(!piste.enabled);
  }, []);

  // ── Événements STOMP ────────────────────────────────────────────────────

  // Ce composant est monté AU-DESSUS de la route /login - un appel entrant doit
  // sonner partout. Au montage, sur l'écran de connexion, il n'y a donc pas de
  // jeton. L'effet ci-dessous sortait alors immédiatement, et comme ses deux
  // dépendances sont stables à vie (`liberer` a des deps vides), il ne se
  // rejouait JAMAIS après la connexion : plus aucun abonnement à la file privée,
  // donc plus une seule offre d'appel reçue de toute la session. On pouvait
  // encore APPELER - c'est un POST plus une sonnerie locale - mais jamais être
  // appelé, et l'appel finissait journalisé en absence.
  const [jeton, setJeton] = useState(() => getToken());
  useEffect(() => surChangementJeton(setJeton), []);

  useEffect(() => {
    if (!jeton) return undefined;
    stompClient.demarrer();
    const off = stompClient.souscrire('/user/queue/notifications', async (ev) => {
      const p = ev.payload || {};
      switch (ev.type) {
        case 'APPEL_OFFRE': {
          if (etatRef.current !== 'inactif') {
            // Déjà en ligne : on décline sans déranger.
            appelService.terminer(p.appel.appelTrackingId, 'OCCUPE').catch(() => { });
            return;
          }
          offreEntrante.current = p.sdp;
          setAppel(p.appel);
          setEtat('appel_entrant');
          arreterSon.current = sonnerieEntrante();
          if (!document.hasFocus() && 'Notification' in window && Notification.permission === 'granted') {
            try { new Notification(texte('appelEntrant'), { body: p.appel.appelantNom, tag: 'cb-appel' }).onclick = () => window.focus(); } catch { /* ignore */ }
          }
          break;
        }
        case 'APPEL_REPONSE': {
          if (!pc.current || p.appel?.appelTrackingId !== appelRef.current?.appelTrackingId) return;
          couperSon();
          clearTimeout(minuterieSonnerie.current);
          setEtat('connexion');
          setAppel(p.appel);
          try {
            await pc.current.setRemoteDescription({ type: 'answer', sdp: p.sdp });
            await rejouerCandidats();
          } catch {
            appelService.terminer(p.appel.appelTrackingId, 'ECHEC_MEDIA').catch(() => { });
            terminerLocalement(texte('motifs.ECHEC'));
          }
          break;
        }
        case 'APPEL_CANDIDAT_ICE': {
          if (p.appelTrackingId !== appelRef.current?.appelTrackingId) return;
          if (pc.current?.remoteDescription) {
            try { await pc.current.addIceCandidate(new RTCIceCandidate(p.candidat)); } catch { /* obsolète */ }
          } else {
            candidatsEnAttente.current.push(p.candidat);
          }
          break;
        }
        case 'APPEL_FIN': {
          if (p.appel?.appelTrackingId !== appelRef.current?.appelTrackingId) return;
          if (etatRef.current === 'inactif' || etatRef.current === 'termine') return;
          terminerLocalement(MOTIFS_LISIBLES[p.appel.issue] || texte('motifs.TERMINE'));
          break;
        }
        default:
      }
    });
    return () => { off(); liberer(); };
  }, [jeton, liberer, terminerLocalement]);

  const valeur = { etat, appel, micCoupe, debutCommunication, motifFin, appeler, accepter, raccrocher, basculerMicro };

  return (
    <ContexteAppel.Provider value={valeur}>
      {children}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioDistant} autoPlay playsInline style={{ display: 'none' }} />
      <FenetreAppel />
    </ContexteAppel.Provider>
  );
}
